import { extractMarkdown } from 'services/imports/extractWithAssistant';
import type { PageLines } from 'services/imports/extractWithAssistant';
import type { ImportPage } from 'services/imports/fileToPages';
import type { ImportTarget } from 'services/imports/markdownContract';
import { runOcrForPages } from 'services/imports/runOcr';
import type { OcrPageState, PageIssue, RunProgress } from 'views/imports/types';
import { toDisplayMessage } from 'views/imports/utils';

const CANCELLED_MESSAGE = 'cancelled';

export type RunExtractionInput = {
  pages: readonly ImportPage[];
  ocrByPage: ReadonlyMap<string, OcrPageState>;
  extractedPageIds: ReadonlySet<string>;
  target: ImportTarget | null;
  extractText: (prompt: string) => Promise<string>;
  scanTables: boolean;
  signal: AbortSignal;
  onProgress: (phase: 'ocr' | 'extracting', progress: RunProgress) => void;
};

export type RunExtractionResult = {
  ocrByPage: Map<string, OcrPageState>;
  markdown: string | null;
  extractedPageIds: Set<string>;
  issues: PageIssue[];
  cancelled: boolean;
};

const needsOcr = (
  page: ImportPage,
  ocrByPage: ReadonlyMap<string, OcrPageState>,
  extractedPageIds: ReadonlySet<string>,
  scanTables: boolean,
): boolean => {
  if (page.failed) return false;
  const state = ocrByPage.get(page.id);
  if (state?.status !== 'done') return true;
  // Pages already extracted keep their OCR: a re-read could shift the L<n> citations already written.
  return scanTables && !state.withTables && !extractedPageIds.has(page.id);
};

export const toPageLines = (ocrByPage: ReadonlyMap<string, OcrPageState>): PageLines => new Map(
  Array.from(ocrByPage.entries())
    .filter(([, state]) => state.status === 'done')
    .map(([pageId, state]) => [pageId, state.lines])
);

export const runExtraction = async (input: RunExtractionInput): Promise<RunExtractionResult> => {
  const { pages, extractedPageIds, target, extractText, scanTables, signal, onProgress } = input;
  const ocrByPage = new Map(input.ocrByPage);
  const issues: PageIssue[] = [];
  const nextExtracted = new Set(extractedPageIds);

  const ocrQueue = pages.filter((page) => needsOcr(page, ocrByPage, extractedPageIds, scanTables));
  if (ocrQueue.length > 0) {
    const results = await runOcrForPages(ocrQueue, {
      onProgress: (completed, total) => onProgress('ocr', { current: completed, total }),
      signal,
      tables: scanTables,
    });
    results.forEach((result) => {
      ocrByPage.set(result.pageId, {
        status: result.status,
        lines: result.lines,
        tables: result.tables,
        withTables: scanTables && result.status === 'done',
        error: result.error,
      });
      if (result.status === 'error') {
        issues.push({ pageId: result.pageId, kind: 'ocr-error', detail: toDisplayMessage(result.error ?? '') });
      }
    });
  }

  if (signal.aborted) {
    return { ocrByPage, markdown: null, extractedPageIds: nextExtracted, issues, cancelled: true };
  }

  // Selected pages are extracted again on request; the merge keeps reviewer edits.
  const queue = pages.filter((page) => ocrByPage.get(page.id)?.status === 'done');
  const lines = toPageLines(ocrByPage);
  const withLines = queue.filter((page) => (lines.get(page.id)?.length ?? 0) > 0).length;
  let calls = 0;

  const guardedExtractText = async (prompt: string): Promise<string> => {
    if (signal.aborted) throw new Error(CANCELLED_MESSAGE);
    try {
      return await extractText(prompt);
    } catch (error) {
      // A request cut short by cancel is a cancellation, not an extraction failure.
      if (signal.aborted) throw new Error(CANCELLED_MESSAGE);
      throw error;
    } finally {
      calls += 1;
      onProgress('extracting', { current: calls, total: withLines });
    }
  };

  // Cached OCR skips the OCR phase, so report the extraction phase before the first model call.
  if (queue.length > 0) onProgress('extracting', { current: 0, total: withLines });
  const result = queue.length === 0
    ? null
    : await extractMarkdown({ pages: queue, lines, extractText: guardedExtractText, target });

  if (result !== null) {
    result.notices.forEach((notice) => issues.push({ pageId: notice.pageId, kind: 'no-lines' }));
    result.errors
      .filter((error) => error.message !== CANCELLED_MESSAGE)
      .forEach((error) => issues.push({ pageId: error.pageId, kind: 'extract-error', detail: toDisplayMessage(error.message) }));
    // Failed pages and pages with no OCR text stay unextracted, so they remain selectable for a retry.
    const unextractedIds = new Set([...result.errors, ...result.notices].map((entry) => entry.pageId));
    queue.filter((page) => !unextractedIds.has(page.id)).forEach((page) => nextExtracted.add(page.id));
  }

  const extractedAny = result !== null && queue.some((page) => nextExtracted.has(page.id));
  return {
    ocrByPage,
    markdown: extractedAny ? result.markdown : null,
    extractedPageIds: nextExtracted,
    issues,
    cancelled: signal.aborted,
  };
};
